import React, { useState, useMemo } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    Grid, Card, CardContent, LinearProgress, Chip, IconButton, Tabs, Tab,
    List, ListItem, ListItemText, ListItemIcon, Paper
} from '@mui/material';
import {
    Close as CloseIcon, Assessment as StatsIcon, TrendingUp as TrendingIcon,
    Language as LanguageIcon, Place as PlaceIcon, EmojiEvents as TrophyIcon,
    CalendarToday as CalendarIcon, Timeline as TimelineIcon,
    Visibility as EyeIcon, CloudUpload as UploadIcon, CheckCircle as CheckIcon, 
    Share as ShareIcon
} from '@mui/icons-material';
import ShareDialog from './ShareDialog';

const StatCard = ({ title, value, subtitle, icon, color = 'primary', progress, maxValue }) => (
    <Card
        sx={{
            height: '100%',
            background: `linear-gradient(135deg, ${color === 'primary' ? '#2563eb15' : color === 'success' ? '#10b98115' : color === 'warning' ? '#f59e0b15' : '#8b5cf615'} 0%, transparent 100%)`,
            border: '1px solid',
            borderColor: `${color}.main`,
            borderRadius: 3,
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 4
            }
        }}
    >
        <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: `${color}.main` }}>
                        {value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" color="text.secondary">
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ color: `${color}.main`, opacity: 0.8 }}>
                    {icon}
                </Box>
            </Box>
            
            {progress !== undefined && maxValue && (
                <Box>
                    <LinearProgress
                        variant="determinate"
                        value={(value / maxValue) * 100}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: `${color}.main`,
                                borderRadius: 3
                            }
                        }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {Math.round((value / maxValue) * 100)}% to next milestone ({maxValue})
                    </Typography>
                </Box>
            )}
        </CardContent>
    </Card>
);

const ActivityHeatmap = ({ activityData }) => {
    const maxCount = Math.max(...activityData.map(d => d.count), 1);
    
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
            {activityData.slice(-30).map((day, index) => {
                const intensity = day.count / maxCount;
                const color = day.count === 0 ? '#f3f4f6' : 
                             intensity < 0.3 ? '#dcfce7' :
                             intensity < 0.6 ? '#86efac' :
                             intensity < 0.8 ? '#22c55e' : '#16a34a';
                
                return (
                    <Box
                        key={index}
                        sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: color,
                            borderRadius: 1,
                            border: '1px solid #e5e7eb',
                            cursor: 'pointer',
                            '&:hover': {
                                transform: 'scale(1.2)',
                                zIndex: 1,
                                boxShadow: 2
                            }
                        }}
                        title={`${new Date(day.date).toLocaleDateString()}: ${day.count} contributions`}
                    />
                );
            })}
        </Box>
    );
};

const LanguageBreakdown = ({ languageStats }) => (
    <List dense>
        {languageStats.slice(0, 5).map(({ language, count }, index) => (
            <ListItem key={language} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <LanguageIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                    primary={language || 'Unknown'}
                    secondary={`${count} contribution${count !== 1 ? 's' : ''}`}
                />
                <Chip
                    label={count}
                    size="small"
                    color={index === 0 ? 'primary' : 'default'}
                    variant={index === 0 ? 'filled' : 'outlined'}
                />
            </ListItem>
        ))}
        {languageStats.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No language data available yet
            </Typography>
        )}
    </List>
);

const PlaceTypeBreakdown = ({ placeTypeStats }) => (
    <List dense>
        {placeTypeStats.slice(0, 5).map(({ type, count }, index) => (
            <ListItem key={type} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                    <PlaceIcon color="success" />
                </ListItemIcon>
                <ListItemText
                    primary={type || 'Unknown'}
                    secondary={`${count} place${count !== 1 ? 's' : ''}`}
                />
                <Chip
                    label={count}
                    size="small"
                    color={index === 0 ? 'success' : 'default'}
                    variant={index === 0 ? 'filled' : 'outlined'}
                />
            </ListItem>
        ))}
        {placeTypeStats.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No place type data available yet
            </Typography>
        )}
    </List>
);

const StatisticsDashboard = ({ open, onClose, statistics }) => {
    const [tabValue, setTabValue] = useState(0);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    const stats = useMemo(() => {
        if (!statistics) return null;
        
        return {
            ...statistics,
            languageStats: statistics.getLanguageStats(),
            placeTypeStats: statistics.getPlaceTypeStats(),
            recentActivity: statistics.getRecentActivity(30),
            achievementProgress: statistics.getAchievementProgress()
        };
    }, [statistics]);

    if (!stats) {
        return (
            <Dialog open={open} onClose={onClose}>
                <DialogContent>
                    <Typography>No statistics available</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    const getMilestoneProgress = (current, milestones) => {
        const nextMilestone = milestones.find(m => m > current);
        return nextMilestone ? { current, next: nextMilestone } : { current, next: milestones[milestones.length - 1] };
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    minHeight: '70vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                m: 0
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StatsIcon />
                    <Typography variant="h6" component="div">
                        Research Statistics
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {/* Tab Navigation */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                        <Tab 
                            label="Overview" 
                            icon={<StatsIcon />}
                            iconPosition="start"
                        />
                        <Tab 
                            label="Activity" 
                            icon={<TimelineIcon />}
                            iconPosition="start"
                        />
                        <Tab 
                            label="Progress" 
                            icon={<TrophyIcon />}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                {/* Overview Tab */}
                {tabValue === 0 && (
                    <Box>
                        {/* Key Metrics */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    title="Total Contributions"
                                    value={stats.totalContributions}
                                    subtitle="Place names added"
                                    icon={<PlaceIcon sx={{ fontSize: 32 }} />}
                                    color="primary"
                                    progress={stats.totalContributions}
                                    maxValue={getMilestoneProgress(stats.totalContributions, [1, 10, 50, 100, 500]).next}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    title="Languages"
                                    value={stats.languagesContributed.size}
                                    subtitle="Different languages"
                                    icon={<LanguageIcon sx={{ fontSize: 32 }} />}
                                    color="success"
                                    progress={stats.languagesContributed.size}
                                    maxValue={getMilestoneProgress(stats.languagesContributed.size, [1, 3, 5, 10]).next}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    title="Current Streak"
                                    value={stats.streakDays}
                                    subtitle={`Longest: ${stats.longestStreak} days`}
                                    icon={<TrendingIcon sx={{ fontSize: 32 }} />}
                                    color="warning"
                                />
                            </Grid>
                        </Grid>

                        {/* Additional Stats */}
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    title="Seen Places"
                                    value={stats.seenPlaces}
                                    subtitle="Marked as visited"
                                    icon={<EyeIcon sx={{ fontSize: 32 }} />}
                                    color="primary"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    title="Wikidata Uploads"
                                    value={stats.wikidataUploads}
                                    subtitle="Shared with community"
                                    icon={<UploadIcon sx={{ fontSize: 32 }} />}
                                    color="success"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <StatCard
                                    title="Validations"
                                    value={stats.validationContributions}
                                    subtitle="Helped verify data"
                                    icon={<CheckIcon sx={{ fontSize: 32 }} />}
                                    color="warning"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                )}

                {/* Activity Tab */}
                {tabValue === 1 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CalendarIcon color="primary" />
                                    Recent Activity (Last 30 Days)
                                </Typography>
                                <ActivityHeatmap activityData={stats.recentActivity} />
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                                    Each square represents a day. Darker = more contributions.
                                </Typography>
                            </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LanguageIcon color="success" />
                                    Top Languages
                                </Typography>
                                <LanguageBreakdown languageStats={stats.languageStats} />
                            </Paper>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PlaceIcon color="warning" />
                                    Place Types Contributed
                                </Typography>
                                <PlaceTypeBreakdown placeTypeStats={stats.placeTypeStats} />
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {/* Progress Tab */}
                {tabValue === 2 && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Typography variant="h6" gutterBottom>Contribution Milestones</Typography>
                                {[1, 10, 50, 100, 500].map(milestone => {
                                    const achieved = stats.totalContributions >= milestone;
                                    const progress = Math.min((stats.totalContributions / milestone) * 100, 100);
                                    
                                    return (
                                        <Box key={milestone} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="body2">
                                                    {milestone} contribution{milestone !== 1 ? 's' : ''}
                                                </Typography>
                                                <Typography variant="body2" color={achieved ? 'success.main' : 'text.secondary'}>
                                                    {achieved ? '✅' : `${stats.totalContributions}/${milestone}`}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={progress}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    backgroundColor: 'grey.200',
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: achieved ? 'success.main' : 'primary.main',
                                                        borderRadius: 4
                                                    }
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Paper>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 3, borderRadius: 3 }}>
                                <Typography variant="h6" gutterBottom>Language Milestones</Typography>
                                {[1, 3, 5, 10].map(milestone => {
                                    const achieved = stats.languagesContributed.size >= milestone;
                                    const progress = Math.min((stats.languagesContributed.size / milestone) * 100, 100);
                                    
                                    return (
                                        <Box key={milestone} sx={{ mb: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="body2">
                                                    {milestone} language{milestone !== 1 ? 's' : ''}
                                                </Typography>
                                                <Typography variant="body2" color={achieved ? 'success.main' : 'text.secondary'}>
                                                    {achieved ? '✅' : `${stats.languagesContributed.size}/${milestone}`}
                                                </Typography>
                                            </Box>
                                            <LinearProgress
                                                variant="determinate"
                                                value={progress}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    backgroundColor: 'grey.200',
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: achieved ? 'success.main' : 'secondary.main',
                                                        borderRadius: 4
                                                    }
                                                }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 3 }}>
                {stats && stats.totalContributions > 0 && (
                    <Button 
                        onClick={() => setShareDialogOpen(true)}
                        variant="outlined" 
                        startIcon={<ShareIcon />}
                        size="large"
                        sx={{ mr: 'auto' }}
                    >
                        Share Progress
                    </Button>
                )}
                <Button onClick={onClose} variant="contained" size="large">
                    Close
                </Button>
            </DialogActions>

            <ShareDialog
                open={shareDialogOpen}
                onClose={() => setShareDialogOpen(false)}
                shareType="stats"
                statistics={statistics}
            />
        </Dialog>
    );
};

export default StatisticsDashboard;